import {
  useState, useRef, useCallback 
} from 'react';
import {
  X,
  Sparkles,
  Loader2,
  ChefHat,
  Check,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  generateRecipeStream,
  type GeneratedRecipe,
  type GenerateRecipeRequest,
} from '../../api/generate-recipe-client';

interface GenAIRecipeModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onRecipeGenerated: (recipe: GeneratedRecipe) => void;
  readonly defaultType?: 'finished' | 'sub-recipe' | 'component';
}

type GenerationStatus = 'idle' | 'generating' | 'success' | 'error';

const CUISINE_OPTIONS = [
  'Any',
  'Italian',
  'French',
  'Asian',
  'Mexican',
  'Mediterranean',
  'American',
  'Indian',
  'Japanese',
  'Thai',
  'Middle Eastern',
];

const DIETARY_OPTIONS = [
  {
    value: 'vegetarian',
    label: 'Vegetarian' 
  },
  {
    value: 'vegan',
    label: 'Vegan' 
  },
  {
    value: 'gluten-free',
    label: 'Gluten-Free' 
  },
  {
    value: 'dairy-free',
    label: 'Dairy-Free' 
  },
  {
    value: 'nut-free',
    label: 'Nut-Free' 
  },
  {
    value: 'low-sodium',
    label: 'Low Sodium' 
  },
  {
    value: 'keto',
    label: 'Keto' 
  },
  {
    value: 'halal',
    label: 'Halal' 
  },
  {
    value: 'kosher',
    label: 'Kosher' 
  },
];

export function GenAIRecipeModal({
  isOpen,
  onClose,
  onRecipeGenerated,
  defaultType = 'finished',
}: GenAIRecipeModalProps) {
  const [prompt, setPrompt] = useState('');
  const [recipeType, setRecipeType] = useState<'finished' | 'sub-recipe' | 'component'>(defaultType);
  const [cuisine, setCuisine] = useState('Any');
  const [dietaryRestrictions, setDietaryRestrictions] = useState<string[]>([]);
  const [status, setStatus] = useState<GenerationStatus>('idle');
  const [streamedContent, setStreamedContent] = useState('');
  const [generatedRecipe, setGeneratedRecipe] = useState<GeneratedRecipe | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleDietaryToggle = (value: string) => {
    setDietaryRestrictions((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );
  };

  const handleGenerate = useCallback(async () => {
    if (prompt.trim() === '') return;

    setStatus('generating');
    setStreamedContent('');
    setGeneratedRecipe(null);
    setError(null);

    abortControllerRef.current = new AbortController();

    const cuisineValue = cuisine === 'Any' ? undefined : cuisine;
    const dietaryValue = dietaryRestrictions.length > 0 ? dietaryRestrictions : undefined;

    const request: GenerateRecipeRequest = {
      prompt: prompt.trim(),
      recipeType,
      cuisine: cuisineValue,
      dietaryRestrictions: dietaryValue,
    };

    await generateRecipeStream(
      request,
      (chunk) => {
        setStreamedContent((prev) => prev + chunk);
      },
      (recipe) => {
        setGeneratedRecipe(recipe);
        setStatus('success');
      },
      (err) => {
        setError(err.message);
        setStatus('error');
      },
      abortControllerRef.current.signal
    );
  }, [prompt, recipeType, cuisine, dietaryRestrictions]);

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setStatus('idle');
  };

  const handleUseRecipe = () => {
    if (generatedRecipe) {
      onRecipeGenerated(generatedRecipe);
      handleClose();
    }
  };

  const handleClose = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setPrompt('');
    setStreamedContent('');
    setGeneratedRecipe(null);
    setError(null);
    setStatus('idle');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Create Recipe with AI</h2>
              <p className="text-sm text-muted-foreground">
                Describe your dish and let AI generate a professional recipe
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-full p-2 hover:bg-muted transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Prompt Input */}
          <div className="space-y-2">
            <label htmlFor="ai-prompt" className="text-sm font-medium">
              Describe your recipe
            </label>
            <textarea
              id="ai-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="E.g., A creamy mushroom risotto with truffle oil, suitable for a fine dining restaurant..."
              className="w-full h-32 rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              disabled={status === 'generating'}
            />
          </div>

          {/* Options Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="recipe-type" className="text-sm font-medium">
                Recipe Type
              </label>
              <select
                id="recipe-type"
                value={recipeType}
                onChange={(e) => setRecipeType(e.target.value as typeof recipeType)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={status === 'generating'}
              >
                <option value="finished">Menu Item</option>
                <option value="sub-recipe">Sub-Recipe</option>
                <option value="component">Component</option>
              </select>
            </div>

            <div className="space-y-2">
              <label htmlFor="cuisine" className="text-sm font-medium">
                Cuisine
              </label>
              <select
                id="cuisine"
                value={cuisine}
                onChange={(e) => setCuisine(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={status === 'generating'}
              >
                {CUISINE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dietary Restrictions */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Dietary Restrictions</label>
            <div className="flex flex-wrap gap-2">
              {DIETARY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleDietaryToggle(option.value)}
                  disabled={status === 'generating'}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    dietaryRestrictions.includes(option.value)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Streaming Output */}
          {(status === 'generating' || streamedContent) && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {status === 'generating' && (
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                )}
                {status === 'success' && (
                  <Check className="h-4 w-4 text-green-500" />
                )}
                <span className="text-sm font-medium">
                  {status === 'generating' ? 'Generating recipe...' : 'Recipe generated'}
                </span>
              </div>
              <div className="rounded-lg border bg-muted/50 p-4 max-h-64 overflow-y-auto">
                <pre className="text-xs whitespace-pre-wrap font-mono">
                  {streamedContent || 'Waiting for response...'}
                </pre>
              </div>
            </div>
          )}

          {/* Error Display */}
          {status === 'error' && error && (
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Generation failed</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {/* Generated Recipe Preview */}
          {generatedRecipe && (
            <div className="rounded-lg border bg-green-50 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <ChefHat className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">{generatedRecipe.name}</span>
              </div>
              <p className="text-sm text-green-700">{generatedRecipe.description}</p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded bg-green-100 text-green-700">
                  {generatedRecipe.servings} servings
                </span>
                <span className="px-2 py-1 rounded bg-green-100 text-green-700">
                  Prep: {generatedRecipe.prepTime}min
                </span>
                <span className="px-2 py-1 rounded bg-green-100 text-green-700">
                  Cook: {generatedRecipe.cookTime}min
                </span>
                <span className="px-2 py-1 rounded bg-green-100 text-green-700">
                  {generatedRecipe.ingredients.length} ingredients
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between border-t px-6 py-4">
          <p className="text-xs text-muted-foreground">
            Powered by Amazon Bedrock
          </p>
          <div className="flex gap-3">
            {status === 'generating' ? (
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            ) : (
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            )}
            
            {status === 'success' && generatedRecipe ? (
              <Button onClick={handleUseRecipe}>
                <Check className="mr-2 h-4 w-4" />
                Use This Recipe
              </Button>
            ) : (
              <Button 
                onClick={handleGenerate} 
                disabled={prompt.trim() === '' || status === 'generating'}
              >
                {status === 'generating' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate Recipe
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
